import { Operation as LoggingOperation, Log, LogCategory } from '@augloop/logging/dist/src/client/logging';
import { WebSocketSessionManagerOptions } from './websocket-session-manager';
import { SessionInitMessage, currentProtocolVersion, IErrorResponse, ISessionInitResponse, Message, ResponseCallback, ErrorResponse, TokenProvisionResponse, TokenProvisionMessage } from '@augloop/session-protocol';
import { ISchemaObject } from '@augloop/types-core';
import { Utils } from '../utils';
import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { TokenType, TokenRefreshManager } from './token-refresh-manager';
import { EventEmitter } from 'eventemitter3';
import { SessionStats } from './isession-manager';

export interface ISessionInitOptions {
    isTokenRefresh: boolean;
    isReconnectOnSameSlice?: boolean;
    extensionConfigs?: ISchemaObject[];
    onResponse?: (error: IErrorResponse, response: ISessionInitResponse) => void;
}

export interface ISessionInitializer {
    initSession(sessionInitOptions: ISessionInitOptions): void;

    /**
     * Emitted when connecting or reconnecting to the service.
     * For new sessions, this triggers reseeding for some hosts.
     * @param event emitted new event
     * @param callback invoked with session data on new
     */
    on(event: 'connect', callback: (isSeedingRequired: boolean, isReconnect: boolean, sessionUrl: string, origin: string, authToken: string) => void): void;

    /**
     * Emitted when reconnecting to the service, regardless of whether it is a new or existing session.
     * @param event emitted reconnect event
     * @param callback invoked on reconnect
     */
    on(event: 'reconnect', callback: () => void): void;
}

export class SessionInitializer extends EventEmitter implements ISessionInitializer {
    private readonly clientMetadata: IClientMetadata;
    private readonly getSessionStats: () => SessionStats;
    private readonly tokenRefreshManager: TokenRefreshManager;
    private sessionKey: string;
    private origin: string;
    private readonly sendMessage: (message: Message, onResponse?: ResponseCallback) => void;
    /** Current value of anonymous user token generated by AL */
    private anonymousToken: string;

    private readonly options: WebSocketSessionManagerOptions;

    public constructor(
        getSessionStats: () => SessionStats,
        clientMetadata: IClientMetadata,
        tokenRefreshManager: TokenRefreshManager,
        sendMessage: (message: Message, onResponse?: ResponseCallback) => void,
        options: WebSocketSessionManagerOptions
    ) {
        super();
        this.getSessionStats = getSessionStats;
        this.clientMetadata = clientMetadata;
        this.tokenRefreshManager = tokenRefreshManager;
        this.sendMessage = sendMessage;
        this.options = options ? options : {};
        this.options.overrideSessionInitMessage = this.options.overrideSessionInitMessage || ((sessionInitMessage) => sessionInitMessage);
    }

    public initSession(sessionInitOptions: ISessionInitOptions): void {
        sessionInitOptions.onResponse = sessionInitOptions.onResponse || (() => {});
        // Telemetry used to understand token refresh and WS reconnect patterns in sessions
        const currentTimeMs = Utils.getCurrentTimeMs();
        const timeSinceLastConnectionClose = this.getSessionStats().lastConnectionClose && (currentTimeMs - this.getSessionStats().lastConnectionClose);
        const timeSinceLastSyncMessage = this.getSessionStats().lastSyncMessage && (currentTimeMs - this.getSessionStats().lastSyncMessage);
        const logOp = new LoggingOperation({
            operationName: 'SessionInit',
        });
        const resultDescription = {
            sessionKey: this.sessionKey,
            sessionId: this.clientMetadata.sessionId,
            timeSinceLastConnectionClose,
            timeSinceLastSyncMessage,
            isTokenRefresh: sessionInitOptions.isTokenRefresh,
            error: undefined
        };

        // Initialize the session specifying the protocol, clientMetadata, sessionKey and token
        // SessionKey & origin will be undefined during the first call
        // Token is either auth token provided by the host or anonymous user token generated by the AL service
        const sessionInitMessage = this.options.overrideSessionInitMessage(
            new SessionInitMessage({
                protocolVersion: currentProtocolVersion,
                clientMetadata: this.clientMetadata,
                sessionKey: this.sessionKey,
                origin: this.origin,
                authToken: this.anonymousToken,
                extensionConfigs: sessionInitOptions.extensionConfigs
            })
        );

        this.sendMessage(sessionInitMessage, (error: IErrorResponse, response: ISessionInitResponse) => {
            logOp.success = !error;
            logOp.resultSignature = this.getSessionStats().lastConnectionClose ? 'Reconnect' : 'FirstConnect';
            logOp.resourceId = response?.sliceUrl;
            // update sessionKey if different than before
            if (response?.sessionKey && resultDescription.sessionKey !== response.sessionKey) {
                resultDescription.sessionKey = response.sessionKey;
            }
            if (error) {
                resultDescription.error = error.error;
            }
            logOp.resultDescription = JSON.stringify(resultDescription);
            Log.info(0x2224d0d7 /* tag_8jndx */, LogCategory.CoreDefault, logOp);

            sessionInitOptions.onResponse(error, response);

            if (error) {
                return;
            }

            // If we tried to reconnect on same slice and got signal to reconnect on global url we will do so and avoid further processing of current response.
            if (!sessionInitOptions.isReconnectOnSameSlice || !response.forceReconnect) {
                // We need both anon token and its expiration time to be valid to proceed
                if (!response.anonymousToken || !response.tokenExpirationSeconds) {
                    Log.error(0x2224d0d8 /* tag_8jndy */, LogCategory.CoreDefault, 'AL Anonymous token was not generated for the session');
                    return;
                }

                this.anonymousToken = response.anonymousToken;
                this.onSuccessfulSessionInitOnServerSide(response);
            }
        });
    }

    private onSuccessfulSessionInitOnServerSide(response: ISessionInitResponse) {
        this.tokenRefreshManager.scheduleRefresh(TokenType.Anonymous, response.tokenExpirationSeconds, this.initSession.bind(this, { isTokenRefresh: true }));

        this.initHostAuthToken();

        // Determine which events to emit
        const isReconnectAttempt: boolean = !!this.sessionKey;
        const isSeedingRequired: boolean = this.sessionKey !== response.sessionKey;

        this.emit(
            'connect',
            isSeedingRequired,
            isReconnectAttempt,
            `${response.sessionUrlBase}/${response.sessionKey}`,
            response.origin,
            this.anonymousToken
        );

        this.sessionKey = response.sessionKey;
        this.origin = response.origin;

        if (isReconnectAttempt) {
            this.emit('reconnect');
        }
    }

    private initHostAuthToken(): void {
        if (this.options.getAuthToken) {
            this.options.getAuthToken()
                .then((hostAuthToken: string) => {
                    if (!hostAuthToken) {
                        Log.info(0x2224d0d9 /* tag_8jndz */, LogCategory.CoreDefault, 'Host auth token provision failed');
                        return;
                    }

                    this.sendTokenProvisionMessage(hostAuthToken);
                })
                .catch(error => {
                    Log.error(0x2224d0da /* tag_8jnd0 */, LogCategory.CoreDefault, `Error happened while attempting to fetch host token: ${error}`);
                });
        }
    }

    private sendTokenProvisionMessage(hostAuthToken: string): void {
        const tokenProvisionMessage: TokenProvisionMessage = new TokenProvisionMessage({
            authToken: hostAuthToken
        });

        this.sendMessage(tokenProvisionMessage, this.onTokenProvisionResponse.bind(this));
    }

    private onTokenProvisionResponse(error: ErrorResponse, response: TokenProvisionResponse): void {
        if (error || !response || !response.tokenExpirationSeconds) {
            return;
        }

        // Establish token refresh which will be triggered once token is close to expiry
        this.tokenRefreshManager.scheduleRefresh(TokenType.Host, response.tokenExpirationSeconds, this.initHostAuthToken.bind(this));
    }
}
